import api from './client';
import type {
  Group,
  GroupMember,
  GroupPost,
  GroupInvite,
  InviteInfo,
  PaginatedResponse,
  GroupSortType,
} from '@/types';
import {
  transformGroup,
  transformGroupMember,
  transformGroupPost,
  transformGroupInvite,
  transformInviteInfo,
  transformPaginatedResponse,
} from '@/lib/utils';

// ==================== GROUP CRUD ====================

export async function createGroup(params: {
  name: string;
  description?: string;
  hashtags?: string[];
  color?: string;
  visibility?: string;
}): Promise<Group> {
  const { data } = await api.post('/groups', params);
  return transformGroup(data) as Group;
}

export async function getGroup(id: string): Promise<Group> {
  const { data } = await api.get(`/groups/${id}`);
  return transformGroup(data) as Group;
}

export async function updateGroup(
  id: string,
  params: {
    name?: string;
    description?: string;
    hashtags?: string[];
    color?: string;
    visibility?: string;
  }
): Promise<Group> {
  const { data } = await api.patch(`/groups/${id}`, params);
  return transformGroup(data) as Group;
}

export async function deleteGroup(id: string): Promise<void> {
  await api.delete(`/groups/${id}`);
}

// ==================== DISCOVERY ====================

export async function browseGroups(
  sort: GroupSortType = 'popular',
  query?: string,
  cursor?: string,
  limit = 20
): Promise<PaginatedResponse<Group>> {
  const params = new URLSearchParams();
  params.set('sort', sort);
  params.set('limit', limit.toString());
  if (query) params.set('q', query);
  if (cursor) params.set('cursor', cursor);

  const { data } = await api.get(`/groups?${params.toString()}`);
  return transformPaginatedResponse(data, transformGroup);
}

export async function getMyGroups(
  cursor?: string,
  limit = 20
): Promise<PaginatedResponse<Group>> {
  const params = new URLSearchParams();
  params.set('limit', limit.toString());
  if (cursor) params.set('cursor', cursor);

  const { data } = await api.get(`/users/me/groups?${params.toString()}`);
  // Backend returns a plain array, not a paginated response
  if (Array.isArray(data)) {
    return {
      items: data.map((item: any) => transformGroup(item) as Group),
      nextCursor: null,
      hasMore: false,
    };
  }
  return transformPaginatedResponse(data, transformGroup);
}

export async function getMyInvites(
  cursor?: string,
  limit = 20
): Promise<PaginatedResponse<Group>> {
  const params = new URLSearchParams();
  params.set('limit', limit.toString());
  if (cursor) params.set('cursor', cursor);

  const { data } = await api.get(`/users/me/groups/invites?${params.toString()}`);
  // Backend returns a plain array, not a paginated response
  if (Array.isArray(data)) {
    return {
      items: data.map((item: any) => transformGroup(item) as Group),
      nextCursor: null,
      hasMore: false,
    };
  }
  return transformPaginatedResponse(data, transformGroup);
}

// ==================== MEMBERSHIP ====================

export async function joinGroup(id: string): Promise<void> {
  await api.post(`/groups/${id}/join`);
}

export async function leaveGroup(id: string): Promise<void> {
  await api.delete(`/groups/${id}/leave`);
}

export async function getGroupMembers(
  groupId: string,
  cursor?: string,
  sort = 'joined',
  limit = 20
): Promise<PaginatedResponse<GroupMember>> {
  const params = new URLSearchParams();
  params.set('sort', sort);
  params.set('limit', limit.toString());
  if (cursor) params.set('cursor', cursor);

  const { data } = await api.get(`/groups/${groupId}/members?${params.toString()}`);
  return transformPaginatedResponse(data, transformGroupMember);
}

export async function approveMember(groupId: string, userId: string): Promise<void> {
  await api.post(`/groups/${groupId}/members/${userId}/approve`);
}

export async function rejectMember(groupId: string, userId: string): Promise<void> {
  await api.delete(`/groups/${groupId}/members/${userId}/reject`);
}

export async function removeMember(groupId: string, userId: string): Promise<void> {
  await api.delete(`/groups/${groupId}/members/${userId}`);
}

export async function banMember(groupId: string, userId: string): Promise<void> {
  await api.post(`/groups/${groupId}/members/${userId}/ban`);
}

export async function unbanMember(groupId: string, userId: string): Promise<void> {
  await api.delete(`/groups/${groupId}/members/${userId}/ban`);
}

export async function muteMember(groupId: string, userId: string): Promise<void> {
  await api.post(`/groups/${groupId}/members/${userId}/mute`);
}

export async function unmuteMember(groupId: string, userId: string): Promise<void> {
  await api.delete(`/groups/${groupId}/members/${userId}/mute`);
}

export async function updateMemberRole(
  groupId: string,
  userId: string,
  role: string
): Promise<void> {
  await api.patch(`/groups/${groupId}/members/${userId}/role`, { role });
}

export async function transferOwnership(groupId: string, userId: string): Promise<void> {
  await api.post(`/groups/${groupId}/transfer`, { new_owner_id: userId });
}

// ==================== INVITATIONS ====================

export async function createInvite(
  groupId: string,
  params?: { max_uses?: number; expires_in_hours?: number }
): Promise<GroupInvite> {
  const { data } = await api.post(`/groups/${groupId}/invites`, params || {});
  return transformGroupInvite(data) as GroupInvite;
}

export async function getInvites(groupId: string): Promise<GroupInvite[]> {
  const { data } = await api.get(`/groups/${groupId}/invites`);
  return (data as any[]).map((i) => transformGroupInvite(i) as GroupInvite);
}

export async function revokeInvite(groupId: string, inviteId: string): Promise<void> {
  await api.delete(`/groups/${groupId}/invites/${inviteId}`);
}

export async function inviteUser(groupId: string, userId: string): Promise<void> {
  await api.post(`/groups/${groupId}/invite-user`, { user_id: userId });
}

export async function getInviteInfo(code: string): Promise<InviteInfo> {
  const { data } = await api.get(`/invites/${code}`);
  return transformInviteInfo(data) as InviteInfo;
}

export async function acceptInvite(code: string): Promise<void> {
  await api.post(`/invites/${code}/accept`);
}

// ==================== GROUP FEED ====================

export async function getGroupFeed(
  groupId: string,
  cursor?: string,
  limit = 20
): Promise<PaginatedResponse<GroupPost>> {
  const params = new URLSearchParams();
  params.set('limit', limit.toString());
  if (cursor) params.set('cursor', cursor);

  const { data } = await api.get(`/groups/${groupId}/feed?${params.toString()}`);
  return transformPaginatedResponse(data, transformGroupPost);
}

export async function postToGroup(groupId: string, blahblahId: string): Promise<void> {
  await api.post(`/groups/${groupId}/posts`, { blahblah_id: blahblahId });
}

export async function deleteGroupPost(groupId: string, postId: string): Promise<void> {
  await api.delete(`/groups/${groupId}/posts/${postId}`);
}

export async function pinPost(groupId: string, postId: string): Promise<void> {
  await api.post(`/groups/${groupId}/posts/${postId}/pin`);
}

export async function unpinPost(groupId: string, postId: string): Promise<void> {
  await api.delete(`/groups/${groupId}/posts/${postId}/pin`);
}

export async function reportPost(groupId: string, postId: string, reason: string): Promise<void> {
  await api.post(`/groups/${groupId}/posts/${postId}/report`, { reason });
}

export async function reportMember(
  groupId: string,
  userId: string,
  reason: string
): Promise<void> {
  await api.post(`/groups/${groupId}/members/${userId}/report`, { reason });
}

// ==================== NOTIFICATIONS ====================

export async function muteGroupNotifications(groupId: string): Promise<void> {
  await api.post(`/groups/${groupId}/mute`);
}

export async function unmuteGroupNotifications(groupId: string): Promise<void> {
  await api.delete(`/groups/${groupId}/mute`);
}
